"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const video_client_core_1 = require("@video/video-client-core");
const mobx_1 = require("mobx");
const online_watcher_1 = require("../utils/online-watcher");
const ui_state_1 = tslib_1.__importDefault(require("../utils/ui-state"));
const api_1 = require("../../api");
class EncoderUiState extends ui_state_1.default {
    constructor(ctrl, options = {}) {
        var _a;
        super();
        this.resolutionsInUseManager = null;
        /*
         *  AudioCtx for silent audio track.
         */
        this.audioCtx = new AudioContext();
        /**
         * Keeps track of previous state for videoDeviceId
         */
        this.prevVideoDeviceId = null;
        /**
         * Indicates whether we are testing the mic or not.
         */
        this.testMic = false;
        (0, video_client_core_1.makeBounded)(this);
        this.mediaStreamController = ctrl;
        this.logger = (_a = options.logger) !== null && _a !== void 0 ? _a : this.mediaStreamController.logger;
        this.init(options);
        (0, mobx_1.makeObservable)(this, {
            // observers
            mediaStreamController: mobx_1.observable.ref,
            prevVideoDeviceId: mobx_1.observable,
            testMic: mobx_1.observable,
            resolutionsInUseManager: mobx_1.observable.ref,
            // computed
            aspectRatioPadding: mobx_1.computed,
            currentAvailableResolutions: mobx_1.computed,
            // actions
            handleScreenCapture: mobx_1.action,
            toggleVideoCallSlider: mobx_1.action,
            handleResolutionsInUseError: mobx_1.action,
        });
        this.logger.trace("constructor()");
    }
    init(options = {}) {
        if (!this.mediaStreamController.inVideoDeviceTransition) {
            if (options.videoDevice === undefined) {
                if (video_client_core_1.mediaController.videoDevices().length > 0) {
                    const [first] = video_client_core_1.mediaController.videoDevices();
                    this.mediaStreamController.videoDeviceId = first.deviceId;
                }
            }
            else {
                this.mediaStreamController.videoDeviceId = options.videoDevice;
            }
        }
        if (!this.mediaStreamController.inAudioDeviceTransition) {
            if (options.audioDevice === undefined) {
                if (video_client_core_1.mediaController.audioDevices().length > 0) {
                    const [first] = video_client_core_1.mediaController.audioDevices();
                    this.mediaStreamController.audioDeviceId = first.deviceId;
                }
            }
            else {
                this.mediaStreamController.audioDeviceId = options.audioDevice;
            }
        }
        this.mediaStreamController.on("error", this.handleMscError);
        this.addInnerDisposer((0, online_watcher_1.onlineWatcher)(this.logger), () => this.audioCtx.close(), () => this.mediaStreamController.off("error", this.handleMscError), this.mediaStreamController);
    }
    /**
     * Indicates whether the video is muted or not.
     */
    get muted() {
        var _a, _b;
        return (_b = (_a = this.videoElement.current) === null || _a === void 0 ? void 0 : _a.muted) !== null && _b !== void 0 ? _b : null;
    }
    /**
     * Current aspect ratio. E.g. 16/9
     */
    get aspectRatioPadding() {
        var _a, _b, _c;
        const ratio = (_c = (_b = (_a = this.mediaStreamController) === null || _a === void 0 ? void 0 : _a.settings) === null || _b === void 0 ? void 0 : _b.video) === null || _c === void 0 ? void 0 : _c.aspectRatio;
        if (ratio == null) {
            return null;
        }
        return `${((1 / ratio) * 100).toFixed(2)}%`;
    }
    /**
     * Toggle mute.
     * @description Toggles the `muted` property on the &lt;video&gt;.
     * @example <caption>Example usage of toggleMute.</caption>
     * toggleMute();
     * // Sets &lt;video&gt; muted property to true|false and updates uiState.muted = true|false;
     */
    toggleMute() {
        if (this.videoElement.current != null) {
            this.videoElement.current.muted = !this.videoElement.current.muted;
        }
    }
    handleMscError(err) {
        switch (err.code) {
            case video_client_core_1.types.ErrorCode.ResolutionsInUse:
                this.handleResolutionsInUseError(err);
                break;
            default:
                break;
        }
    }
    handleResolutionsInUseError(err) {
        var _a;
        this.logger.debug("handleResolutionsInUseError()", { err });
        if (this.mediaStreamController != null) {
            if (this.resolutionsInUseManager == null) {
                (0, mobx_1.runInAction)(() => {
                    this.resolutionsInUseManager = new api_1.ResolutionsInUseManager({ msc: this.mediaStreamController });
                });
            }
            const { videoDevice, allAvailableResolutions, currentAvailableResolutions } = err;
            (_a = this.resolutionsInUseManager) === null || _a === void 0 ? void 0 : _a.addDeviceInUse({
                videoDevice,
                allAvailableResolutions,
                currentAvailableResolutions,
            });
        }
    }
    /**
     * @todo: Add logging for mediaController in case of mediaStreamController being null.
     * Toggle `videoDeviceId`.
     * @description Toggles the encoder to/from screenCapture and sets uiState.videoDeviceId to
     * either the previous videoDeviceId or screencapture.
     * @example <caption>Example usage of handleScreenCapture.</caption>
     * handleScreenCapture();
     */
    handleScreenCapture() {
        var _a, _b;
        if (((_a = this.mediaStreamController) === null || _a === void 0 ? void 0 : _a.videoDeviceId) != null) {
            if (((_b = this.mediaStreamController) === null || _b === void 0 ? void 0 : _b.videoDeviceId) !== "screencapture") {
                this.prevVideoDeviceId = this.mediaStreamController.videoDeviceId;
                this.mediaStreamController.videoDeviceId = "screencapture";
            }
            else {
                this.mediaStreamController.videoDeviceId = this.prevVideoDeviceId;
            }
        }
    }
    get currentAvailableResolutions() {
        var _a, _b;
        return (_b = (_a = this.resolutionsInUseManager) === null || _a === void 0 ? void 0 : _a.currentAvailableResolutions) !== null && _b !== void 0 ? _b : this.mediaStreamController.availableResolutions;
    }
    /**
     * Toggle `viewVideoCallSlider`.
     * @description Toggles the video call settings slider to/from visible and sets uiState.viewVideoCallSlider
     * to true/false.
     * @example <caption>Example usage of toggleVideoCallSlider.</caption>
     * toggleVideoCallSlider();
     * // Sets video call settings slider to/from visible and updates uiState.viewVideoCallSlider = true|false;
     */
    toggleVideoCallSlider() {
        this.viewVideoCallSlider = !this.viewVideoCallSlider;
    }
}
EncoderUiState.displayName = "EncoderUiState";
exports.default = EncoderUiState;
//# sourceMappingURL=ui-state.js.map